import * as yup from 'yup';
import 'yup-phone';
import {
	noRuEmailDomainValidator,
	noCiryllicCharsEmailDomainValidator,
} from '../../validators/email.validators';
import { uniqBy } from 'lodash';
import { studentValidationSchema } from '../Student/student.validation.schema';

yup.addMethod(yup.array, 'uniqueByEmail', function(message) {
    return this.test('uniqueByEmail', message, function(list) {
		list = list?.filter(l => !!l.email);
		if (list?.length === 0) {
			return true;
		}

		list?.forEach(l => { l.isDublicate = false });
		const sameEmailStudents = uniqBy(list, 'email');
		const noDublicates = sameEmailStudents?.length === list?.length;
		if (!noDublicates) {
			list = list?.map(l => {
				const emailCount = list?.filter(lf => lf.email === l.email);
				l.isDublicate = emailCount && emailCount.length > 1;
				return l;
			})
		}
		return noDublicates;
    });
});

declare module 'yup' {
	// tslint:disable-next-line
	interface ArraySchema<T> {
	  uniqueByEmail(
		// eslint-disable-next-line no-unused-vars
		message?: string,
	  ): ArraySchema<T>;
	}
}

export const studyRequestValidationSchema = {
	military: yup.object().shape({
		name: yup.string().required(),
		email: yup
			.string()
			.email()
			.required()
			.test(noRuEmailDomainValidator)
			.test(noCiryllicCharsEmailDomainValidator),
		phone: yup.string().phone('UA', true).required(),
		unit: yup
			.string()
			.required()
			.matches(/^([Aa\u0410\u0430]|[Ee\u0415\u0435]|[Tt\u0422\u0442])?\d{4}[Tt\u0422\u0442]?$/g, {
				message: 'Wrong Unit format.',
			}),
		students: yup.array().min(1).of(studentValidationSchema).uniqueByEmail('duplicate student email'),
		requestAttachments: yup.array().min(1).required(),
		agreement: yup.boolean().isTrue().required(),
	}),
	civil: yup.object({
		name: yup.string().required(),
		email: yup
			.string()
			.email()
			.required()
			.test(noRuEmailDomainValidator)
			.test(noCiryllicCharsEmailDomainValidator),
		phone: yup.string().phone('UA', true).required(),
		unit: yup.string().required(),
		students: yup.array().min(1).of(studentValidationSchema).uniqueByEmail('duplicate student email'),
		requestAttachments: yup.array().min(1).required(),
		agreement: yup.boolean().isTrue().required(),
	}),
};

export const studyRequestValidationSchemaWithNotes = {
	military: yup.object().shape({
		name: yup.string().required(),
		email: yup
			.string()
			.email()
			.required()
			.test(noRuEmailDomainValidator)
			.test(noCiryllicCharsEmailDomainValidator),
		phone: yup.string().phone('UA', true).required(),
		students: yup.array().min(1).of(studentValidationSchema).uniqueByEmail('duplicate student email'),
		unit: yup
			.string()
			.required()
			.matches(/^([Aa\u0410\u0430]|[Ee\u0415\u0435]|[Tt\u0422\u0442])?\d{4}[Tt\u0422\u0442]?$/g, { message: 'Wrong Unit format.' }),
		notes: yup.string().required(),
	}),
	civil: yup.object({
		name: yup.string().required(),
		email: yup
			.string()
			.email()
			.required()
			.test(noRuEmailDomainValidator)
			.test(noCiryllicCharsEmailDomainValidator),
		phone: yup.string().phone('UA', true).required(),
		students: yup.array().min(1).of(studentValidationSchema).uniqueByEmail('duplicate student email'),
		unit: yup.string().required(),
		notes: yup.string().required(),
	}),
};

export const studyRequestBasicValidationSchema = {
	military: yup.object().shape({
		name: yup.string().required(),
		email: yup
			.string()
			.email()
			.required()
			.test(noRuEmailDomainValidator)
			.test(noCiryllicCharsEmailDomainValidator),
		phone: yup.string().phone('UA', true).required(),
		students: yup.array().min(1).of(studentValidationSchema).uniqueByEmail('duplicate student email'),
		unit: yup
			.string()
			.required()
			.matches(/^([Aa\u0410\u0430]|[Ee\u0415\u0435]|[Tt\u0422\u0442])?\d{4}[Tt\u0422\u0442]?$/g, { message: 'Wrong Unit format.' }),
		agreement: yup.boolean().isTrue().required(),
	}),
	civil: yup.object({
		name: yup.string().required(),
		email: yup
			.string()
			.email()
			.required()
			.test(noRuEmailDomainValidator)
			.test(noCiryllicCharsEmailDomainValidator),
		phone: yup.string().phone('UA', true).required(),
		students: yup.array().min(1).of(studentValidationSchema).uniqueByEmail('duplicate student email'),
		unit: yup.string().required(),
		agreement: yup.boolean().isTrue().required(),
	}),
};

export const studyRequestAdminValidationSchema = {
	military: yup.object().shape({
		name: yup.string().required(),
		email: yup
			.string()
			.email()
			.required()
			.test(noRuEmailDomainValidator)
			.test(noCiryllicCharsEmailDomainValidator),
		phone: yup.string().phone('UA', true).required(),
		students: yup.array().min(1).of(studentValidationSchema).uniqueByEmail('duplicate student email'),
		unit: yup
			.string()
			.required()
			.matches(/^([Aa\u0410\u0430]|[Ee\u0415\u0435]|[Tt\u0422\u0442])?\d{4}[Tt\u0422\u0442]?$/g, { message: 'Wrong Unit format.' }),
		agreement: yup.boolean().isTrue().optional(),
	}),
	civil: yup.object({
		name: yup.string().required(),
		email: yup
			.string()
			.email()
			.required()
			.test(noRuEmailDomainValidator)
			.test(noCiryllicCharsEmailDomainValidator),
		phone: yup.string().phone('UA', true).required(),
		students: yup.array().min(1).of(studentValidationSchema).uniqueByEmail('duplicate student email'),
		unit: yup.string().required(),
		agreement: yup.boolean().isTrue().optional(),
	}),
};

